import React, { useState, useEffect } from 'react';
import { ChevronUp, ChevronDown, MessageSquare, Info } from 'lucide-react';
import { customerService } from '../../../services/customerService';
import { organizationService } from '../../../services/organizationService';
import { notesService } from '../../../services/notesService';
import { ActiveCustomer } from '../../../types/organizations';
import { getCategoryStyling } from '../../../utils/categoryStyling';
import RiskProfileModal from '../../common/modals/RiskProfileModal';
import DunningModal from '../../common/modals/DunningModal';
import { CustomerCreditTermsLimitModal } from '../modals';
import NotesModal from '../../common/modals/NotesModal';

interface CustomerRubrics {
  ltm_invoices: string;
  receivable: number;
  ltm_value: number;
  first_order_date: string | null;
  first_order_days_ago: string | null;
  dpd: string;
  otpr: string;
  dbt: string;
  ptp: string;
  credit_risk_profile?: 'NEW' | 'RED' | 'AMBER' | 'GREEN' | 'No Dunning';
  credit_risk_profile_reasonings?: string[];
}

interface CustomerWithRubrics extends ActiveCustomer {
  rubrics: CustomerRubrics;
}

interface CustomersPSETabProps {
  organizationId: string;
  accessToken: string | null;
  setUserAndToken: (user: any, token: string) => void;
  onCustomerNoteCountChange?: (customerId: string, delta: number) => void;
}

interface SortConfig {
  field: string;
  direction: 'asc' | 'desc';
}

const CustomersPSETab: React.FC<CustomersPSETabProps> = ({
  organizationId,
  accessToken,
  setUserAndToken,
  onCustomerNoteCountChange
}) => {
  const [allCustomers, setAllCustomers] = useState<CustomerWithRubrics[]>([]);
  const [filteredCustomers, setFilteredCustomers] = useState<CustomerWithRubrics[]>([]);
  const [displayedCustomers, setDisplayedCustomers] = useState<CustomerWithRubrics[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [sortConfig, setSortConfig] = useState<SortConfig[]>([{ field: 'first_order_date', direction: 'asc' }]);
  const [pageSize] = useState(20);
  const [showRiskModal, setShowRiskModal] = useState(false);
  const [modalReasonings, setModalReasonings] = useState<string[]>([]);
  const [modalProfile, setModalProfile] = useState<'NEW' | 'RED' | 'AMBER' | 'GREEN' | 'No Dunning' | undefined>(undefined);
  const [showDunningModal, setShowDunningModal] = useState(false);
  const [dunningReasonings, setDunningReasonings] = useState<string[]>([]);
  const [riskFilter, setRiskFilter] = useState<'All' | 'NEW' | 'RED' | 'AMBER' | 'GREEN' | 'No Dunning'>('All');
  const [searchQuery, setSearchQuery] = useState('');
  const [baseCreditTerms, setBaseCreditTerms] = useState<string | null>(null);
  const [baseCreditTermsDesc, setBaseCreditTermsDesc] = useState<string | null>(null);
  const [baseCreditLimit, setBaseCreditLimit] = useState<number | null>(null);
  const [baseCreditLimitDesc, setBaseCreditLimitDesc] = useState<string | null>(null);
  const [showCreditInfoModal, setShowCreditInfoModal] = useState(false);
  const [creditInfoCustomer, setCreditInfoCustomer] = useState<CustomerWithRubrics | null>(null);
  const [creditInfoInitialValues, setCreditInfoInitialValues] = useState<{ credit_terms?: string | null; credit_terms_desc?: string | null; credit_limit?: number | null; credit_limit_desc?: string | null; hasIndividualized?: boolean } | null>(null);
  
  // Notes modal state
  const [showNotesModal, setShowNotesModal] = useState(false);
  const [selectedCustomerForNotes, setSelectedCustomerForNotes] = useState<CustomerWithRubrics | null>(null);
  const [customerNotesCount, setCustomerNotesCount] = useState<Record<string, number>>({});

  const fetchCustomers = async (forceRefresh: boolean = false) => {
    if (!organizationId || !accessToken) return;

    // Check if we have cached data (unless force refresh is requested)
    const cacheKey = `customers_pse_${organizationId}`;
    const cachedData = localStorage.getItem(cacheKey);
    const cacheTimestamp = localStorage.getItem(`${cacheKey}_timestamp`);
    
    // Check if cache is still valid (60 minutes)
    const now = Date.now();
    const cacheAge = cacheTimestamp ? now - parseInt(cacheTimestamp) : Infinity;
    const isCacheValid = cacheAge < 60 * 60 * 1000; // 60 minutes
    
    console.log('Cache check:', { 
      hasCachedData: !!cachedData, 
      cacheAge: Math.round(cacheAge / 1000) + 's', 
      isCacheValid, 
      forceRefresh 
    });

    if (!forceRefresh && cachedData && isCacheValid) {
      try {
        const parsedData = JSON.parse(cachedData);
        console.log('Loading customers from cache:', parsedData.length, 'customers');
        setAllCustomers(parsedData);
        setError('');
        setLoading(false);
        return;
      } catch (err) {
        // If parsing fails, continue with API call
        console.warn('Failed to parse cached data:', err);
      }
    }

    try {
      setLoading(true);
      console.log('Fetching customers from API...');
      const response = await customerService.getAllCustomersForPSE(
        organizationId,
        accessToken,
        setUserAndToken
      );
      
      console.log('API response:', response.customers.length, 'customers');
      setAllCustomers(response.customers as CustomerWithRubrics[]);
      setError('');
      
      // Cache the response
      localStorage.setItem(cacheKey, JSON.stringify(response.customers));
      localStorage.setItem(`${cacheKey}_timestamp`, now.toString());
    } catch (err: any) {
      console.error('API error:', err);
      setError(err.message || 'Failed to fetch customers');
    } finally {
      setLoading(false);
    }
  };

  const sortCustomers = (customers: CustomerWithRubrics[], sortConfig: SortConfig[]) => {
    if (sortConfig.length === 0) return customers;

    return [...customers].sort((a, b) => {
      for (const sort of sortConfig) {
        let aValue: any;
        let bValue: any;

        switch (sort.field) {
          case 'name':
            aValue = a.name.toLowerCase();
            bValue = b.name.toLowerCase();
            break;
          case 'ltm_invoices':
            // Parse the LTM invoices string (e.g., "5/10" or "0/0")
            const aLtmInvoices = a.rubrics?.ltm_invoices || '0/0';
            const bLtmInvoices = b.rubrics?.ltm_invoices || '0/0';
            const aLtmCount = parseInt(aLtmInvoices.split('/')[0]) || 0;
            const bLtmCount = parseInt(bLtmInvoices.split('/')[0]) || 0;
            aValue = aLtmCount;
            bValue = bLtmCount;
            break;
          case 'receivable':
            aValue = a.rubrics?.receivable || 0;
            bValue = b.rubrics?.receivable || 0;
            break;
          case 'ltm_value':
            aValue = a.rubrics?.ltm_value || 0;
            bValue = b.rubrics?.ltm_value || 0;
            break;
          case 'first_order_date': {
            // Sort by days ago (N/A first, then by days ago value)
            const aDaysAgo = a.rubrics?.first_order_days_ago;
            const bDaysAgo = b.rubrics?.first_order_days_ago;
            
            // N/A values go first (treat as 0)
            const aIsNA = !aDaysAgo || aDaysAgo === 'N/A';
            const bIsNA = !bDaysAgo || bDaysAgo === 'N/A';
            
            if (aIsNA && !bIsNA) return -1; // N/A goes first
            if (!aIsNA && bIsNA) return 1;
            if (aIsNA && bIsNA) return 0; // Both N/A, equal
            
            // Parse days ago values (extract number from "X days ago" format)
            const aDays = aDaysAgo ? parseInt(aDaysAgo.replace(/\D/g, '')) || 0 : 0;
            const bDays = bDaysAgo ? parseInt(bDaysAgo.replace(/\D/g, '')) || 0 : 0;
            
            aValue = aDays;
            bValue = bDays;
            break;
          }
          case 'otpr': {
            const aText = a.rubrics?.otpr || 'N/A';
            const bText = b.rubrics?.otpr || 'N/A';
            const aIsNA = aText === 'N/A';
            const bIsNA = bText === 'N/A';
            if (aIsNA && !bIsNA) return 1; // N/A goes last
            if (!aIsNA && bIsNA) return -1;
            aValue = parseFloat(aText.replace('%', '')) || 0;
            bValue = parseFloat(bText.replace('%', '')) || 0;
            break;
          }
          case 'dbt':
            aValue = parseFloat(a.rubrics?.dbt || '0');
            bValue = parseFloat(b.rubrics?.dbt || '0');
            break;
          default:
            return 0;
        }

        if (aValue < bValue) return sort.direction === 'asc' ? -1 : 1;
        if (aValue > bValue) return sort.direction === 'asc' ? 1 : -1;
      }
      return 0;
    });
  };

  const applySortingAndPagination = () => {
    let filtered = allCustomers;

    // Filter by credit risk profile
    if (riskFilter !== 'All') {
      filtered = filtered.filter(c => (c.rubrics?.credit_risk_profile || '') === riskFilter);
    }

    // Filter by search query (name search)
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase().trim();
      filtered = filtered.filter(customer => 
        customer.name.toLowerCase().includes(query)
      );
    }

    // Sort customers
    const sorted = sortCustomers(filtered, sortConfig);
    setFilteredCustomers(sorted);
    
    // Apply pagination
    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    const paginated = sorted.slice(startIndex, endIndex);
    setDisplayedCustomers(paginated);
    
    // Update pagination info
    setTotalCount(sorted.length);
    setTotalPages(Math.ceil(sorted.length / pageSize));
  };

  useEffect(() => {
    fetchCustomers();
  }, [organizationId, accessToken]);

  // Fetch base credit terms/limit once
  useEffect(() => {
    const loadOrg = async () => {
      if (!organizationId || !accessToken) return;
      try {
        const org = await organizationService.getOrganization(organizationId, accessToken, setUserAndToken);
        setBaseCreditTerms(org.base_credit_terms || null);
        setBaseCreditTermsDesc(org.base_credit_terms_desc || null);
        setBaseCreditLimit(org.base_credit_limit ?? null);
        setBaseCreditLimitDesc(org.base_credit_limit_desc || null);
      } catch {}
    };
    loadOrg();
  }, [organizationId, accessToken, setUserAndToken]);

  // No separate fetch; data is embedded in customers from API

  useEffect(() => {
    if (allCustomers.length > 0) {
      applySortingAndPagination();
    }
  }, [allCustomers, sortConfig, currentPage, riskFilter, searchQuery]);

  // Load notes count for all customers
  useEffect(() => {
    allCustomers.forEach(customer => {
      loadCustomerNotesCount(customer.id);
    });
  }, [allCustomers]);

  const handleSort = (field: string) => {
    const existingSort = sortConfig.find(s => s.field === field);
    let newSortConfig: SortConfig[];

    if (existingSort) {
      // Toggle direction or remove if already desc
      if (existingSort.direction === 'asc') {
        existingSort.direction = 'desc';
        newSortConfig = [...sortConfig];
      } else {
        // Remove the sort
        newSortConfig = sortConfig.filter(s => s.field !== field);
      }
    } else {
      // Add new sort
      newSortConfig = [...sortConfig, { field, direction: 'asc' }];
    }

    setSortConfig(newSortConfig);
    setCurrentPage(1); // Reset to first page when sorting changes
  };

  // Reset to first page when search or filter changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, riskFilter]);

  const getSortIcon = (field: string) => {
    const sort = sortConfig.find(s => s.field === field);
    if (!sort) return null;
    return sort.direction === 'asc' ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />;
  };


  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'N/A';
    return dateString;
  };

  const handleCustomerClick = (customer: CustomerWithRubrics) => {
    window.open(`/organizations/${organizationId}/customers/${customer.id}`, '_blank');
  };

  const riskChipClasses = (profile?: 'NEW' | 'RED' | 'AMBER' | 'GREEN') => {
    if (!profile) return 'bg-gray-100 text-gray-800 border border-gray-200';
    
    const styling = getCategoryStyling(profile);
    return `${styling.bg} ${styling.text} border border-gray-200`;
  };

  const openRiskModal = (profile?: 'NEW' | 'RED' | 'AMBER' | 'GREEN', reasonings?: string[]) => {
    setModalProfile(profile);
    // Order reasonings by evaluation sequence: NEW -> RED -> AMBER -> GREEN
    const order = [
      /^NEW/i, /^Not NEW/i,
      /^RED/i, /^Not RED/i,
      /^AMBER/i, /^Not AMBER/i,
      /^GREEN/i, /^Not GREEN/i
    ];
    const items = [...(reasonings || [])];
    const ranked = items.sort((a, b) => {
      const ai = order.findIndex((re) => re.test(a));
      const bi = order.findIndex((re) => re.test(b));
      const aIdx = ai === -1 ? 999 : ai;
      const bIdx = bi === -1 ? 999 : bi;
      if (aIdx !== bIdx) return aIdx - bIdx;
      return a.localeCompare(b);
    });
    setModalReasonings(ranked);
    setShowRiskModal(true);
  };

  const openDunningModal = (reasonings?: string[]) => {
    setDunningReasonings(reasonings || []);
    setShowDunningModal(true);
  };

  // Handle notes modal
  const handleOpenNotesModal = (customer: CustomerWithRubrics) => {
    setSelectedCustomerForNotes(customer);
    setShowNotesModal(true);
  };

  const handleCloseNotesModal = () => {
    setShowNotesModal(false);
    setSelectedCustomerForNotes(null);
  };

  // Load notes count for a customer - now using embedded note_count from customer object
  const loadCustomerNotesCount = async (customerId: string) => {
    // Find the customer in the current data and use its embedded note_count
    const customer = allCustomers.find(c => c.id === customerId);
    if (customer && 'note_count' in customer) {
      setCustomerNotesCount(prev => ({
        ...prev,
        [customerId]: customer.note_count || 0
      }));
    }
  };

  const handlePageChange = (page: number) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mr-3"></div>
        <span className="text-gray-600">Loading customers...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
        {error}
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="mb-4 flex justify-between items-center">
        <div>
          <h3 className="text-lg font-medium text-gray-900">Credit Controlling Rubrics - All Customers</h3>
          <p className="text-sm text-gray-600"><b>90 days</b> rolling period.</p>
        </div>
        <div className="flex items-center space-x-3">
          {/* Search by Name */}
          <div className="flex items-center space-x-2">
            <label className="text-sm text-gray-600">Search:</label>
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search by name..."
              className="text-sm border border-gray-300 rounded-md px-2 py-1 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          <div className="flex items-center space-x-2">
            <label className="text-sm text-gray-600">Risk Profile:</label>
            <div className="relative">
              <select
                value={riskFilter}
                onChange={(e) => { setRiskFilter(e.target.value as any); setCurrentPage(1); }}
                className="text-sm border border-gray-300 rounded-md px-2 py-1 bg-white appearance-none pr-8 cursor-pointer"
              >
                <option value="All">All</option>
                <option value="NEW">NEW</option>
                <option value="RED">RED</option>
                <option value="AMBER">AMBER</option>
                <option value="GREEN">GREEN</option>
                <option value="No Dunning">No Dunning</option>
              </select>
              <div className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
                <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </div>
            </div>
          </div>
          <button
            onClick={() => {
              // Clear PSE cache and force refresh
              const cacheKey = `customers_pse_${organizationId}`;
              localStorage.removeItem(cacheKey);
              localStorage.removeItem(`${cacheKey}_timestamp`);
              
              fetchCustomers(true);
            }}
            className="px-4 py-2 text-sm font-medium text-blue-600 bg-blue-50 border border-blue-200 rounded-md hover:bg-blue-100 transition-colors"
          >
            Refresh Data
          </button>
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200 table-fixed">
          <thead className="bg-gray-50">
            <tr>
              <th 
                className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 w-48"
                onClick={() => handleSort('name')}
              >
                <div className="flex items-center">
                  Customer Name {getSortIcon('name')}
                </div>
              </th>
              <th 
                className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 w-24"
                onClick={() => handleSort('ltm_invoices')}
              >
                <div className="flex items-center">
                  LTM Invoices {getSortIcon('ltm_invoices')}
                </div>
              </th>
              <th 
                className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 w-40"
                onClick={() => handleSort('receivable')}
              >
                <div className="flex items-center">
                  Receivable / LTM Value {getSortIcon('receivable')}
                </div>
              </th>
              <th 
                className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 w-32"
                onClick={() => handleSort('first_order_date')}
              >
                <div className="flex items-center">
                  First Order Date {getSortIcon('first_order_date')}
                </div>
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-32">Credit Terms / Limit</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-40">Days Past Due (DPD)</th>
              <th 
                className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 w-32"
                onClick={() => handleSort('otpr')}
              >
                <div className="flex items-center">
                  On-time Payment Rate (OTPR) {getSortIcon('otpr')}
                </div>
              </th>
              <th 
                className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 w-40"
                onClick={() => handleSort('dbt')}
              >
                <div className="flex items-center">
                  Average Days Beyond Terms (DBT) {getSortIcon('dbt')}
                </div>
              </th>
              
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-36">
                Credit Risk Profile
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {displayedCustomers.map((customer) => (
              <tr key={customer.id} className="hover:bg-gray-50">
                <td className="px-4 py-4">
                  <div className="space-y-1">
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => handleCustomerClick(customer)}
                        className="text-blue-600 hover:text-blue-800 hover:underline font-medium text-left flex-1"
                      >
                        {customer.name}
                      </button>
                      {/* Notes Button */}
                      <button
                        onClick={() => handleOpenNotesModal(customer)}
                        className={`p-1 rounded transition-colors ${
                          customerNotesCount[customer.id] > 0 
                            ? 'bg-blue-100 text-blue-600 hover:bg-blue-200' 
                            : 'bg-gray-100 text-gray-400 hover:bg-gray-200'
                        }`}
                        title={customerNotesCount[customer.id] > 0 ? `${customerNotesCount[customer.id]} note${customerNotesCount[customer.id] !== 1 ? 's' : ''}` : 'Add note'}
                      >
                        <MessageSquare className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </td>
                <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                  {customer.rubrics?.ltm_invoices || 'N/A'}
                </td>
                <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                  {formatCurrency(customer.rubrics?.receivable || 0)} / {formatCurrency(customer.rubrics?.ltm_value || 0)}
                </td>
                <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                  <div>
                    {formatDate(customer.rubrics?.first_order_date)}
                    {customer.rubrics?.first_order_days_ago && (
                      <div className="text-xs text-gray-500">
                        ({customer.rubrics.first_order_days_ago})
                      </div>
                    )}
                  </div>
                </td>
                <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                  {(() => {
                    const rec = (customer as any).individualized_credit;
                    const isActive = rec && rec.status === 'active';
                    const terms = isActive && rec?.credit_terms ? rec.credit_terms : (baseCreditTerms || 'N/A');
                    const limit = isActive && rec?.credit_limit != null ? rec.credit_limit : (baseCreditLimit ?? null);
                    const termsDesc = isActive && rec?.credit_terms_desc ? rec.credit_terms_desc : (baseCreditTermsDesc || '');
                    const limitDesc = isActive && rec?.credit_limit_desc ? rec.credit_limit_desc : (baseCreditLimitDesc || '');
                    const values = {
                      credit_terms: terms,
                      credit_terms_desc: termsDesc,
                      credit_limit: limit ?? null,
                      credit_limit_desc: limitDesc,
                      hasIndividualized: isActive
                    };
                    return (
                      <div className={`flex items-center space-x-2 ${isActive ? 'bg-blue-50 text-blue-700 px-2 py-1 rounded' : ''}`}>
                        <div className="flex items-center space-x-1">
                          <span>{terms || 'N/A'}</span>
                        </div>
                        <span>/</span>
                        <div className="flex items-center space-x-1">
                          <span>{limit != null ? new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(limit) : 'N/A'}</span>
                        </div>
                        {isActive && (termsDesc || limitDesc) && (
                          <button
                            className="text-gray-400 hover:text-gray-600 ml-2"
                            title="View details"
                            onClick={() => { setCreditInfoCustomer(customer); setCreditInfoInitialValues(values); setShowCreditInfoModal(true); }}
                          >
                            <Info className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    );
                  })()}
                </td>
                <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                  <div>
                    {(customer.rubrics?.dpd || 'N/A').split('<br />').map((line, index, array) => (
                      <React.Fragment key={index}>
                        {line}
                        {index < array.length - 1 && <br />}
                      </React.Fragment>
                    ))}
                  </div>
                </td>
                <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                  {customer.rubrics?.otpr || 'N/A'}
                </td>
                <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                  {customer.rubrics?.dbt || 'N/A'}
                </td>
                
                <td className="px-4 py-4 whitespace-nowrap text-sm">
                  <button
                    type="button"
                    onClick={() => {
                      // If credit risk profile is "No Dunning", show dunning modal, otherwise show risk modal
                      if (customer.rubrics?.credit_risk_profile === 'No Dunning') {
                        openDunningModal(customer.rubrics?.credit_risk_profile_reasonings);
                      } else {
                        openRiskModal(customer.rubrics?.credit_risk_profile, customer.rubrics?.credit_risk_profile_reasonings);
                      }
                    }}
                    className={`px-2 py-1 rounded-full text-xs font-semibold ${
                      customer.rubrics?.credit_risk_profile === 'No Dunning'
                        ? 'bg-blue-100 text-blue-800 border border-blue-200 hover:bg-blue-200'
                        : riskChipClasses(customer.rubrics?.credit_risk_profile)
                    }`}
                    title={customer.rubrics?.credit_risk_profile === 'No Dunning' ? "View dunning reasonings" : "View reasonings"}
                  >
                    {customer.rubrics?.credit_risk_profile || 'N/A'}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <RiskProfileModal
        isOpen={showRiskModal}
        onClose={() => setShowRiskModal(false)}
        profile={modalProfile}
        reasonings={modalReasonings}
        organizationId={organizationId}
      />

      {/* Read-only credit terms/limit modal */}
      {showCreditInfoModal && creditInfoCustomer && creditInfoInitialValues && (
        <CustomerCreditTermsLimitModal
          isOpen={showCreditInfoModal}
          onClose={() => setShowCreditInfoModal(false)}
          organizationId={organizationId}
          xeroDirectContactId={creditInfoCustomer.id}
          accessToken={accessToken}
          setUserAndToken={setUserAndToken}
          readOnly={true}
          initialValues={creditInfoInitialValues}
        />
      )}

      <DunningModal
        isOpen={showDunningModal}
        onClose={() => setShowDunningModal(false)}
        reasonings={dunningReasonings}
      />

      {/* Notes Modal */}
      {showNotesModal && selectedCustomerForNotes && (
        <NotesModal
          isOpen={showNotesModal}
          onClose={handleCloseNotesModal}
          entityType="customer"
          entityId={selectedCustomerForNotes.id}
          entityName={selectedCustomerForNotes.name}
          organizationId={organizationId}
          onNoteCountChange={(delta: number) => {
            // Update local state
            setCustomerNotesCount(prev => ({
              ...prev,
              [selectedCustomerForNotes.id]: Math.max(0, (prev[selectedCustomerForNotes.id] || 0) + delta)
            }));
            // Update customer data cache
            setAllCustomers(prev => {
              const updatedCustomers = prev.map(customer => 
                customer.id === selectedCustomerForNotes.id 
                  ? { ...customer, note_count: Math.max(0, (customer.note_count || 0) + delta) }
                  : customer
              );
              
              // Update localStorage cache
              const cacheKey = `customers_pse_${organizationId}`;
              localStorage.setItem(cacheKey, JSON.stringify(updatedCustomers));
              
              return updatedCustomers;
            });
            // Update parent customer data cache
            if (onCustomerNoteCountChange) {
              onCustomerNoteCountChange(selectedCustomerForNotes.id, delta);
            }
          }}
        />
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between mt-6">
          <div className="text-sm text-gray-700">
            Showing {((currentPage - 1) * pageSize) + 1} to {Math.min(currentPage * pageSize, totalCount)} of {totalCount} customers
          </div>
          <div className="flex space-x-2">
            <button
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
              className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Previous
            </button>
            
            {/* Page numbers */}
            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
              const pageNum = i + 1;
              return (
                <button
                  key={pageNum}
                  onClick={() => handlePageChange(pageNum)}
                  className={`px-3 py-2 text-sm font-medium rounded-md ${
                    currentPage === pageNum
                      ? 'bg-blue-600 text-white'
                      : 'text-gray-500 bg-white border border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  {pageNum}
                </button>
              );
            })}
            
            {totalPages > 5 && (
              <>
                <span className="px-3 py-2 text-sm text-gray-500">...</span>
                <button
                  onClick={() => handlePageChange(totalPages)}
                  className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                >
                  {totalPages}
                </button>
              </>
            )}
            
            <button
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
              className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
            </button>
          </div>
        </div>
      )}

      {displayedCustomers.length === 0 && !loading && (
        <div className="text-center py-12">
          <div className="text-gray-500 text-lg mb-2">No customers found</div>
          <div className="text-gray-400 text-sm">No customers match the current criteria.</div>
        </div>
      )}
    </div>
  );
};

export default CustomersPSETab;
